import { useContext, useMemo, useState, useCallback, MouseEvent } from 'react'
import moment from 'moment-timezone'
import { map } from 'ramda'
import { useTranslation } from 'react-i18next'
import cn from 'classnames/bind'

import {
  Table,
  Tr,
  TdLabel,
  Th,
  TrSelectable,
  useColumnsWidthsTo,
} from 'app/common/ui-components/table'

import { useMonthDaysAsStrings } from 'app/common/hooks/useMonthDays'
import toFixedValue from 'app/common/lib/toFixedValue'
import { BannerContent } from 'app/common/components-next/banner'
import { useCurrencySign } from 'app/common/components-next/currency'
import { PlainSideSlider } from 'app/common/ui-next/plain-side-slider'
import Sticky from 'app/common/templates-next/sticky'
import { PageTemplateContent } from 'app/common/templates-next/page-template'

import { PurchasesApi } from '../PurchasesApi'
import { SubsidiesContext, type Subsidy } from '../CafeteriaSubsidiesData'
import CafeteriaSubsidyOrdersData from '../CafeteriaSubsidyOrdersData'
import SubsidyIndicator from './SubsidyIndicator'
import ItemDetails from './ItemDetails'

import styles from './CafeteriaSubsidiesTable.module.scss';

const cx = cn.bind(styles)

type TableRowData = {
  day: string
  dateKey: string
} & Subsidy

type Props = {
  date: string
  personId: number
  timezone: string
  schoolId?: string
}

const CafeteriaSubsidiesTable = ({
  // stopUseGPU,
  date,
  personId,
  timezone,
  schoolId,
}: Props) => {
  const { t } = useTranslation('cafeteria')
  const currency = useCurrencySign('commaSpaceBefore')
  const currentYear = moment(date).get('year')
  const days = useMonthDaysAsStrings(date)
  const {
    content: { subsidies },
    loading: { subsidies: loadingSubsidies },
  } = useContext(SubsidiesContext)
  const loading = loadingSubsidies

  const tableHeaders = t('subsidies_table_columns', { returnObjects: true })

  const subsidiesContent: Array<TableRowData> = useMemo(
    () =>
      map((day: string) => {
        const dateKey = moment(day, 'DD.MM').set('year', currentYear).format('YYYY-MM-DD')
        return {
          ...subsidies[dateKey],
          day,
          dateKey,
        }
      })(days),
    [days, currentYear, subsidies],
  )

  const [refFrom, refTo] = useColumnsWidthsTo()
  const [details, setDetails] = useState('') // dateKey

  const handleChangeDetails = useCallback(
    (id: string, checked: boolean) => {
      // if (!subsidies[id].total) {
      //   setDetails('');
      //   return;
      // }
      if (details === id) {
        setDetails(checked ? id : '')
        return
      }
      setDetails(id)
    },
    [details],
  )

  const handleCloseDetails = useCallback((e: MouseEvent<HTMLElement>) => {
    e.preventDefault()
    setDetails('')
  }, [])

  return (
    <>
      <Sticky>
        {(ref, sticky, shadow) => (
          <PageTemplateContent
            reference={ref}
            shadowBottom={shadow}
            className={cx({ sticky })}
            skipPadding
            flex
          >
            <div className={styles.tableContainer}>
              <Table className={styles.table}>
                <thead>
                  <Tr
                    header
                    // @ts-ignore
                    reference={refTo}
                    headerHasBorderBottom={false}
                  >
                    <Th alignLeft id="header1" className={styles.th}>
                      {tableHeaders[0]}
                    </Th>
                    <Th alignLeft id="header2" className={styles.th}>
                      {tableHeaders[1]}
                      {currency}
                    </Th>
                    <Th alignLeft id="header3" className={styles.th}>
                      {tableHeaders[2]}
                    </Th>
                    <Th alignLeft id="header4" className={styles.th}>
                      {tableHeaders[3]}
                    </Th>
                  </Tr>
                </thead>
              </Table>
            </div>

            <BannerContent
              placeId="d099e4c6-f44b-42ef-b5f9-a1d52c27dee5"
              className={styles.bannerPlaceholder}
              displayAsPlaceholder
              height={0}
            />
          </PageTemplateContent>
        )}
      </Sticky>
      <PageTemplateContent className={styles.layout} skipPadding flex>
        <div className={styles.tableContainer}>
          <Table className={styles.table}>
            <thead>
              <Tr
                header
                hidden
                // @ts-ignore
                reference={refFrom}
              >
                <Th alignLeft className={styles.th}>
                  {tableHeaders[0]}
                </Th>
                <Th alignLeft className={styles.th}>
                  {tableHeaders[1]}
                  {currency}
                </Th>
                <Th alignLeft className={styles.th}>
                  {tableHeaders[2]}
                </Th>
                <Th alignLeft className={styles.th}>
                  {tableHeaders[3]}
                </Th>
              </Tr>
            </thead>
            <tbody>
              {subsidiesContent.map(
                ({
                  day,
                  comment,
                  order_number: orderNumber,
                  order_id: orderId,
                  total,
                  spent,
                  dateKey,
                }) => {
                  const sTotal = total || total === 0 ? toFixedValue(total) : null
                  const sSpent = spent || spent === 0 ? toFixedValue(spent) : null

                  const rowProps = {
                    key: day,
                    id: dateKey,
                    onClick: handleChangeDetails,
                    selected: details ? dateKey === details : false,
                    classNameFocused: styles.focusedRow,
                    loading,
                  }

                  return (
                    <TrSelectable {...rowProps}>
                      <TdLabel htmlFor={dateKey} headers="header1" className={styles.dayCell}>
                        {day}
                      </TdLabel>

                      <TdLabel htmlFor={dateKey} headers="header2">
                        <SubsidyIndicator available={total || 0} used={spent || 0} />
                        &nbsp;
                        {sTotal && sSpent && `${sSpent}/${sTotal}`}
                        {!sTotal && sSpent}
                        {!sTotal && !sSpent && '0.00'}
                      </TdLabel>

                      <TdLabel htmlFor={dateKey} headers="header3">
                        {orderNumber || '—'}
                      </TdLabel>

                      <TdLabel htmlFor={dateKey} headers="header4">
                        {comment ||
                          (!orderId
                            ? t('subsidies_table_order_absent', 'Отсутствует справка')
                            : '')}
                      </TdLabel>
                    </TrSelectable>
                  )
                },
              )}
            </tbody>
          </Table>
        </div>

        <BannerContent
          // leaveContainerOnEmpty
          refreshOnMount
          className={styles.bannerContent}
          placeId="d099e4c6-f44b-42ef-b5f9-a1d52c27dee5"
        />
      </PageTemplateContent>

      <PlainSideSlider
        open={!!details}
        onCrossClick={handleCloseDetails}
        position={{
          top: 0,
          right: 0,
          bottom: 0,
        }}
      >
        <CafeteriaSubsidyOrdersData
          personId={personId}
          schoolId={schoolId ? parseInt(schoolId, 10) : undefined}
        >
          <PurchasesApi
            personId={personId}
            // @ts-ignore
            schoolId={schoolId}
            from={details}
            to={details}
            // status={statuses.SPENT}
            autoRequest
          >
            <ItemDetails date={details} timezone={timezone} />
          </PurchasesApi>
        </CafeteriaSubsidyOrdersData>
      </PlainSideSlider>
    </>
  )
}

export default CafeteriaSubsidiesTable
